import React, { useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';

// --- Common Components ---
import SplashScreen from './auth/SplashScreen';
import LoginScreen from './auth/LoginScreen';
import CreateUserScreen from './auth/CreateUserScreen';
import RegisterScreen from './auth/RegisterScreen';
import ConsentsScreen from './auth/ConsentsScreen';
import ESignScreen from './auth/ESignScreen';
import CipNoticeScreen from './auth/CipNoticeScreen';
import VerifyEmailScreen from './onboarding/VerifyEmailScreen';
import OnboardingComplete from './onboarding/OnboardingComplete';

import CrossBorderOnboarding from './onboarding/cross_border/CrossBorderOnboarding';
import BusinessOnboarding from './onboarding/business/BusinessOnboarding';

// --- Auth ---
import ProtectedRoute from './common/ProtectedRoute';

// --- Dashboard Layout ---
import DashboardLayout from './common/templates/DashboardLayout';

// --- Eager Shared Screens ---
// --- Eager Shared Screens ---
import ProfileScreen from './dashboard/ProfileScreen';
import MyAccountScreen from './dashboard/MyAccountScreen';
import PlaceholderScreen from './dashboard/PlaceholderScreen';
import ProfileSettingsScreen from './dashboard/ProfileSettingsScreen';

// --- Lazy-Loaded Screens (Code Splitting) ---
const TransferMenuScreen = React.lazy(() => import('./dashboard/TransferMenuScreen'));
const TransferScreen = React.lazy(() => import('./dashboard/TransferScreen'));
const CryptoScreen = React.lazy(() => import('./dashboard/CryptoScreen'));
const CryptoTradeScreen = React.lazy(() => import('./dashboard/CryptoTradeScreen'));
const PartnerDashboardScreen = React.lazy(() => import('./dashboard/PartnerDashboardScreen'));

const GlobalStyle = createGlobalStyle`
  body, html {
    margin: 0;
    padding: 0;
    width: 100%;
    overflow-x: hidden !important;
  }
  
  * {
    box-sizing: border-box;
  }
`;

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Fallback skeleton for lazy-loaded components
const FallbackLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#6b7280' }}>
    Loading module...
  </div>
);

// Wrapper for repeated route children
const DashboardRoutes = ({ mode }) => (
  <Suspense fallback={<FallbackLoader />}>
    <Routes>
      <Route path="home" element={<ProfileScreen mode={mode} />} />
      <Route path="accounts" element={<MyAccountScreen mode={mode} />} />
      <Route path="transfer" element={<TransferMenuScreen mode={mode} />} />
      <Route path="transfer/domestic" element={<TransferScreen mode={mode} category="domestic" />} />
      <Route path="transfer/international" element={<TransferScreen mode={mode} category="international" />} />
      <Route path="transfer/mass" element={<PlaceholderScreen title="Mass Payments" icon="💸" description="Execute batch payments via CSV upload." />} />
      <Route path="crypto" element={<CryptoScreen mode={mode} />} />
      <Route path="crypto/trade" element={<CryptoTradeScreen mode={mode} />} />
      <Route path="compliance" element={<PlaceholderScreen title="Compliance" icon="🛡️" description="KYC/KYB document management, AML monitoring, and sanctions screening." />} />
      <Route path="treasury" element={<PlaceholderScreen title="Treasury" icon="📊" description="Cash flow forecasting, liquidity management, and FX exposure dashboard." />} />
      <Route path="fx" element={<PlaceholderScreen title="Foreign Exchange" icon="💱" description="Real-time currency conversion with institutional rates." comingSoon />} />
      <Route path="investments" element={<PlaceholderScreen title="Investments" icon="📈" description="Portfolio management, structured products, and yield optimization." comingSoon />} />
      <Route path="cards" element={<PlaceholderScreen title="Cards" icon="💳" description="Virtual and physical card management, spend controls, and statements." comingSoon />} />
      <Route path="partner" element={<PartnerDashboardScreen mode={mode} />} />
      <Route path="settings" element={<ProfileSettingsScreen />} />
      <Route path="*" element={<Navigate to="home" replace />} />
    </Routes>
  </Suspense>
);

/**
 * Main component for the Progressive Web App (PWA).
 */
export default function MobileApp() {
  useEffect(() => {
    const setVH = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    setVH();
    window.addEventListener('resize', setVH);
    return () => window.removeEventListener('resize', setVH);
  }, []);

  return (
    <>
      <GlobalStyle />
      <ScrollToTop />
      <Routes>
        {/* Onboarding and Login Flow */}
        <Route path="/splash" element={<SplashScreen />} />
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<LoginScreen />} />

        <Route path="/create-user" element={<CreateUserScreen />} />
        <Route path="/register" element={<RegisterScreen />} />
        <Route path="/consents" element={<ConsentsScreen />} />
        <Route path="/e-sign" element={<ESignScreen />} />
        <Route path="/cip-notice" element={<CipNoticeScreen />} />
        <Route path="/verify-email" element={<VerifyEmailScreen />} />
        <Route path="/create/cross_border" element={<CrossBorderOnboarding />} />
        <Route path="/create/business" element={<BusinessOnboarding />} />
        <Route path="/create/completed" element={<OnboardingComplete />} />

        {/* --- Authenticated Dashboards Wrapped in Dynamic Layout --- */}
        <Route path="/cross_border/*" element={<ProtectedRoute><DashboardLayout mode="cross_border" /></ProtectedRoute>}>
          <Route path="*" element={<DashboardRoutes mode="cross_border" />} />
        </Route>

        <Route path="/business/*" element={<ProtectedRoute><DashboardLayout mode="business" /></ProtectedRoute>}>
          <Route path="*" element={<DashboardRoutes mode="business" />} />
        </Route>
      </Routes>
    </>
  );
}
